import { z } from 'zod';
import { isoDateTime, uuid } from './common.js';
// --- hand-out (P2.5: QR2 brought inside; HO-01 to HO-08) ---------------------------------------------
export const handoutScope = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('all') }),
    z.object({ kind: z.literal('department'), departments: z.array(z.string().max(100)).min(1).max(200) }),
    z.object({ kind: z.literal('zip'), from: z.number().int().min(0).max(99999), to: z.number().int().min(0).max(99999), zone: z.string().max(100).nullable().optional() }),
]);
export const HANDOUT_STATUSES = ['pending', 'handed_out', 'approval_required', 'cancelled'];
export const handoutStatus = z.enum(HANDOUT_STATUSES);
export const HANDOUT_STATUS_LABELS = { pending: 'Mangler', handed_out: 'Udleveret', approval_required: 'Kræver godkendelse', cancelled: 'Annulleret' };
export const handoutOrderDto = z.object({
    id: uuid,
    number: z.number().int(),
    token: z.string().nullable(),
    shopId: uuid,
    shopName: z.string(),
    name: z.string(),
    email: z.string().nullable(),
    username: z.string().nullable(),
    zip: z.string().nullable(),
    department: z.string().nullable(),
    zone: z.string().nullable(),
    products: z.array(z.object({ title: z.string(), quantity: z.number().int(), image: z.string().nullable() })),
    status: handoutStatus,
    handedOutAt: isoDateTime.nullable(),
    handedOutByName: z.string().nullable(),
    note: z.string().nullable(),
    /** Last day a take-back is allowed; null when there is no limit or nothing to take back. */
    recallUntil: isoDateTime.nullable(),
    canRecall: z.boolean(),
});
export const HANDOUT_RESULTS = ['fulfilled', 'already_fulfilled', 'approval_required', 'cancelled', 'recalled', 'not_handed_out', 'recall_expired'];
export const handoutResultDto = z.object({ result: z.enum(HANDOUT_RESULTS), message: z.string(), order: handoutOrderDto });
export const handoutActionRequest = z.object({ note: z.string().trim().max(500).optional(), app: z.enum(['scan', 'editor']).default('scan') });
export const handoutBulkRequest = z.object({ orderIds: z.array(uuid).min(1).max(50), note: z.string().trim().max(500).optional() });
export const handoutEventDto = z.object({
    id: uuid,
    at: isoDateTime,
    userName: z.string(),
    action: z.string(),
    note: z.string().nullable(),
    orderId: uuid,
    orderNumber: z.number().int(),
    name: z.string().nullable(),
    batchId: uuid.nullable(),
    app: z.string(),
});
export const handoutTodayDto = z.object({ events: z.array(handoutEventDto), handouts: z.number().int(), recalls: z.number().int(), refused: z.number().int() });
export const handoutOverviewDto = z.object({
    total: z.number().int(),
    handedOut: z.number().int(),
    missing: z.number().int(),
    approval: z.number().int(),
    cancelled: z.number().int(),
    today: z.number().int(),
    yesterday: z.number().int(),
    recallsToday: z.number().int(),
    days: z.array(z.object({ date: z.string(), handouts: z.number().int(), recalls: z.number().int() })),
    byDepartment: z.array(z.object({ department: z.string(), total: z.number().int(), missing: z.number().int() })),
    byZone: z.array(z.object({ zoneId: uuid.nullable(), name: z.string(), zipFrom: z.number().int().nullable(), zipTo: z.number().int().nullable(), total: z.number().int(), missing: z.number().int() })),
    scoped: z.boolean(),
});
const csv = z.string().max(5000).optional();
export const handoutOrderQuery = z.object({
    shopId: uuid,
    status: csv,
    zipFrom: z.coerce.number().int().optional(),
    zipTo: z.coerce.number().int().optional(),
    departments: csv,
    products: csv,
    q: z.string().trim().max(100).optional(),
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(5000).default(200),
});
export const handoutOrderListDto = z.object({ items: z.array(handoutOrderDto), total: z.number().int(), products: z.array(z.string()), departments: z.array(z.string()), missingZip: z.number().int() });
export const handoutDrawerDto = z.object({ order: handoutOrderDto, history: z.array(handoutEventDto), qrSvg: z.string().nullable() });
export const HANDOUT_COLUMNS = ['number', 'name', 'username', 'email', 'zip', 'zone', 'department', 'product', 'status', 'handedOutBy', 'handedOutAt', 'note'];
export const HANDOUT_COLUMN_LABELS = { number: 'Ordrenr.', name: 'Navn', username: 'Brugernavn', email: 'E-mail', zip: 'Postnr.', zone: 'Zone', department: 'Afdeling', product: 'Gave', status: 'Status', handedOutBy: 'Udleveret af', handedOutAt: 'Udleveret', note: 'Note' };
export const handoutExportQuery = handoutOrderQuery.extend({ format: z.enum(['xlsx', 'csv']).default('xlsx'), columns: csv, zone: z.string().max(100).optional() });
export const handoutZoneDto = z.object({ id: uuid, shopId: uuid, name: z.string(), zipFrom: z.number().int(), zipTo: z.number().int(), sortOrder: z.number().int() });
export const saveHandoutZoneRequest = z.object({ name: z.string().trim().min(1).max(100), zipFrom: z.number().int().min(0).max(99999), zipTo: z.number().int().min(0).max(99999), sortOrder: z.number().int().default(100) }).refine((z2) => z2.zipFrom <= z2.zipTo, { message: 'Fra skal være mindre end til', path: ['zipTo'] });
export const handoutPresetDto = z.object({ id: uuid, ownerId: uuid, ownerName: z.string(), shopId: uuid.nullable(), name: z.string(), filters: z.record(z.string(), z.unknown()), columns: z.array(z.string()), format: z.enum(['xlsx', 'csv']), shared: z.boolean(), mine: z.boolean() });
export const saveHandoutPresetRequest = z.object({ shopId: uuid.nullable(), name: z.string().trim().min(1).max(100), filters: z.record(z.string(), z.unknown()).default({}), columns: z.array(z.enum(HANDOUT_COLUMNS)).default([]), format: z.enum(['xlsx', 'csv']).default('xlsx'), shared: z.boolean().default(false) });
export const handoutUserDto = z.object({ id: uuid, name: z.string(), email: z.string(), status: z.string(), roles: z.array(z.string()), shopId: uuid.nullable(), shopName: z.string().nullable(), scope: handoutScope.nullable(), lastLoginAt: isoDateTime.nullable() });
export const saveHandoutUserRequest = z.object({ shopId: uuid.nullable(), scope: handoutScope.nullable() });
export const importHandoutUsersRequest = z.object({ emails: z.string().max(50000), role: z.enum(['handout_scanner', 'handout_editor']).default('handout_scanner'), shopId: uuid, scope: handoutScope.nullable().optional() });
export const handoutLogQuery = z.object({ shopId: uuid, from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(), to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(), userId: uuid.optional(), action: z.string().max(40).optional(), q: z.string().trim().max(100).optional(), page: z.coerce.number().int().min(1).default(1), perPage: z.coerce.number().int().min(1).max(500).default(100) });
export const handoutContextDto = z.object({ shops: z.array(z.object({ id: uuid, name: z.string() })), shopId: uuid.nullable(), scope: handoutScope.nullable(), recallDays: z.number().int(), zones: z.array(handoutZoneDto) });
//# sourceMappingURL=handout.js.map